import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SavedChartState } from '../types';

export interface PDFExportOptions {
  format: 'a4' | 'letter' | 'legal';
  orientation: 'portrait' | 'landscape';
  measuresPerLine: number;
  linesPerPage: number;
  headerFontSize: number;  // Title, artist, style, key, time
  chordsFontSize: number;  // Chords in measures
  lyricsFontSize: number;  // Lyrics in measures
  includeChords: boolean;
  includeLyrics: boolean;
  includeAnnotations: boolean;
}

/**
 * Export a chart to PDF format using VECTOR GRAPHICS (not images)
 * This creates smaller file sizes and scalable, sharp text
 * Enhanced with proper overlapping prevention and pagination
 */
export async function exportChartToPDFVector(
  chart: SavedChartState,
  options: PDFExportOptions,
  previewOnly: boolean = false
): Promise<jsPDF | void> {
  try {
    console.log('📄 Starting VECTOR PDF export...', options);

    const { format, orientation } = options;

    // Create PDF with specified format and orientation
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format,
    });

    renderPDFContent(pdf, chart, options);

    // If preview only, return the PDF object for display
    if (previewOnly) {
      console.log('✅ VECTOR PDF preview generated!');
      return pdf;
    }

    // Otherwise, save the PDF to download
    const filename = sanitizeFilename(chart.songTitle || 'chart') + '.pdf';
    console.log('💾 Saving VECTOR PDF as:', filename);
    pdf.save(filename);

    console.log('✅ VECTOR PDF exported successfully!');
  } catch (error) {
    console.error('❌ Error exporting VECTOR PDF:', error);
    throw new Error('Failed to export PDF. Please try again.');
  }
}

/**
 * Render PDF content with proper pagination
 */
function renderPDFContent(
  pdf: jsPDF,
  chart: SavedChartState,
  options: PDFExportOptions
): void {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const usableWidth = pageWidth - (2 * margin);

  const { measuresPerLine, headerFontSize, chordsFontSize, lyricsFontSize } = options;
  const measureWidth = usableWidth / measuresPerLine;
  // Smart compact height: minimum 15mm, grows if needed to fit chords + lyrics
  // Font sizes are in pt; convert to mm (1pt = 0.3528mm) before computing layout height
  const PT_TO_MM = 0.3528;
  const lineHeight = Math.max(15, (chordsFontSize + lyricsFontSize) * PT_TO_MM * 1.8);

  let currentY = margin;
  let measureIndex = 0;
  let currentPage = 1;

  // Title
  pdf.setFontSize(headerFontSize + 4);
  pdf.setFont('helvetica', 'bold');
  pdf.text(chart.songTitle, margin, currentY);
  currentY += headerFontSize * 0.7;

  // Subtitle
  if (chart.songAuthor || chart.songStyle) {
    pdf.setFontSize(headerFontSize);
    pdf.setFont('helvetica', 'normal');
    const subtitle = [chart.songAuthor, chart.songStyle].filter(Boolean).join(' • ');
    pdf.text(subtitle, margin, currentY);
    currentY += headerFontSize * 0.7;
  }

  // Key and Time
  pdf.setFontSize(headerFontSize - 1);
  pdf.setFont('helvetica', 'italic');
  const metadata = `Key: ${chart.keySignature} | Time: ${chart.timeSignature.beats}/${chart.timeSignature.beatType}`;
  pdf.text(metadata, margin, currentY);
  currentY += headerFontSize * 0.9;

  // Separator
  pdf.setDrawColor(150, 150, 150);
  pdf.setLineWidth(0.3);
  pdf.line(margin, currentY, pageWidth - margin, currentY);
  currentY += headerFontSize * 0.7;

  // Render measures with pagination
  while (measureIndex < chart.sourceChart.length) {
    // Check if we need a new page BEFORE starting a line
    if (currentY + lineHeight > pageHeight - margin - 10) {
      pdf.addPage();
      currentPage++;
      currentY = margin;
    }

    // Render one line of measures
    const measuresInLine = Math.min(measuresPerLine, chart.sourceChart.length - measureIndex);
    
    renderMeasureLine(pdf, chart.sourceChart.slice(measureIndex, measureIndex + measuresInLine), {
      x: margin,
      y: currentY,
      measureWidth,
      lineHeight,
      options,
    });

    measureIndex += measuresInLine;
    currentY += lineHeight + 2; // Compact spacing between lines
  }

  // Footer with page numbers
  const totalPages = pdf.getNumberOfPages();
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(headerFontSize - 2);
  
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    const footerText = `Page ${i}/${totalPages}`;
    pdf.text(footerText, pageWidth / 2, pageHeight - 5, { align: 'center' });
  }
}

/**
 * Normalize Unicode musical symbols to ASCII equivalents that Helvetica supports.
 * ♭ → b, ♯ → #, ♮ → (removed), ° and ø are Latin-1 and supported.
 */
function normalizePDFChord(chord: string): string {
  return chord
    .replace(/♭/g, 'b')
    .replace(/♯/g, '#')
    .replace(/♮/g, '');
}

/**
 * Parse and render a styled chord symbol with proper formatting
 * (root normal, accidentals smaller, extensions as superscript)
 * Based on StyledChord.tsx logic
 */
function renderStyledChord(
  pdf: jsPDF,
  chord: string,
  x: number,
  y: number,
  baseFontSize: number
): void {
  // Normalize Unicode symbols that Helvetica can't render
  const normalizedChord = normalizePDFChord(chord);

  // Special case: repeat symbol
  if (normalizedChord === '%') {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(baseFontSize * 1.5);
    pdf.text('%', x, y, { align: 'center' });
    return;
  }

  // Parse chord structure
  let mainChordPart = normalizedChord;
  let slashBassPart: string | null = null;

  // Extract slash bass (e.g., C/G)
  const lastSlashIndex = normalizedChord.lastIndexOf('/');
  if (lastSlashIndex > 0 && !normalizedChord.includes('6/9')) {
    const potentialBass = normalizedChord.substring(lastSlashIndex + 1);
    if (potentialBass.match(/^[A-G][#b]?.*$/)) {
      mainChordPart = normalizedChord.substring(0, lastSlashIndex);
      slashBassPart = `/${potentialBass}`;
    }
  }

  // Parse root + accidental + remainder
  const rootRegex = /^([A-G])([#b]?)(.*)/;
  const match = mainChordPart.match(rootRegex);

  if (!match) {
    // Fallback: render as plain text
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(baseFontSize);
    pdf.text(normalizedChord, x, y, { align: 'center' });
    return;
  }

  const [, root, accidental, remainder] = match;

  // Separate quality base (m, maj) from extensions (7, 9, etc.)
  let qualityBase = '';
  let qualitySup = '';

  if (remainder.startsWith('maj')) {
    qualityBase = 'maj';
    qualitySup = remainder.substring(3);
  } else if (remainder.startsWith('m') && !remainder.startsWith('maj')) {
    qualityBase = 'm';
    qualitySup = remainder.substring(1);
  } else {
    // Everything else is superscript (dim, aug, 7, 9, sus, etc.)
    qualitySup = remainder;
  }

  // Font sizes for different parts
  const extensionFontSize = baseFontSize * 0.6;
  const accidentalFontSize = baseFontSize * 0.65;

  // ── Calculate TOTAL chord width first, then center ──────────────────────
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(baseFontSize);
  const rootWidth = pdf.getTextWidth(root);

  pdf.setFontSize(accidentalFontSize);
  const accidentalWidth = accidental ? pdf.getTextWidth(accidental) : 0;

  pdf.setFontSize(extensionFontSize);
  const qualityBaseWidth = qualityBase ? pdf.getTextWidth(qualityBase) : 0;
  const qualitySupWidth = qualitySup ? pdf.getTextWidth(qualitySup) : 0;

  const totalWidth = rootWidth + accidentalWidth + qualityBaseWidth + qualitySupWidth;
  let currentX = x - totalWidth / 2; // Center the ENTIRE chord
  // ────────────────────────────────────────────────────────────────────────

  // Render ROOT
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(baseFontSize);
  pdf.text(root, currentX, y);
  currentX += rootWidth;

  // Render ACCIDENTAL (slightly smaller and raised)
  if (accidental) {
    pdf.setFontSize(accidentalFontSize);
    pdf.text(accidental, currentX, y - (baseFontSize * 0.15));
    currentX += accidentalWidth;
  }

  // Render QUALITY BASE (m, maj) at baseline
  if (qualityBase) {
    pdf.setFontSize(extensionFontSize);
    pdf.text(qualityBase, currentX, y);
    currentX += qualityBaseWidth;
  }

  // Render EXTENSIONS as superscript
  if (qualitySup) {
    pdf.setFontSize(extensionFontSize);
    pdf.text(qualitySup, currentX, y - (baseFontSize * 0.3));
  }

  // Render SLASH BASS — left-aligned under root, close to main chord baseline
  if (slashBassPart) {
    const bassX = x - totalWidth / 2;        // Same left edge as root (not centered)
    const bassY = y + baseFontSize * 0.2;    // Small drop: ~3mm at 16pt
    pdf.setFontSize(baseFontSize * 0.65);    // Matches accidental size (more readable)
    pdf.text(normalizePDFChord(slashBassPart), bassX, bassY);
  }
}

/**
 * Chord positions as [topPct, leftPct] within the measure box.
 * Mirrors the app's X-divider / diagonal-divider layout (Measure.tsx incenter positions).
 * 1 chord: center
 * 2 chords: diagonal (TL triangle / BR triangle)
 * 3 chords: X with 3 (TL, TR, bottom-center)
 * 4 chords: diamond/X (left, top, bottom, right)
 */
const CHORD_POSITIONS: [number, number][][] = [
  [],                                    // 0
  [[50, 50]],                            // 1 — center
  [[29, 29], [71, 71]],                  // 2 — diagonal TL / BR
  [[25, 25], [25, 75], [75, 50]],        // 3 — X: TL, TR, bottom-C
  [[50, 21], [21, 50], [79, 50], [50, 79]], // 4 — diamond: L, T, B, R
];

/** Font scale factor when multiple chords share a measure */
const CHORD_FONT_SCALE = [1, 1, 0.75, 0.67, 0.60];

/** Draw diagonal (2 chords) or X (3–4 chords) divider lines inside a measure box */
function drawMeasureDividers(
  pdf: jsPDF,
  mx: number, my: number,
  mw: number, mh: number,
  count: number
): void {
  pdf.setDrawColor(130, 130, 130);
  pdf.setLineWidth(0.15);
  if (count === 2) {
    pdf.line(mx, my, mx + mw, my + mh);           // \ diagonal
  } else {
    pdf.line(mx, my, mx + mw, my + mh);           // \ diagonal
    pdf.line(mx + mw, my, mx, my + mh);           // / diagonal
  }
}

/**
 * Render a line of measures with proper spacing to avoid overlaps
 */
function renderMeasureLine(
  pdf: jsPDF,
  measures: SavedChartState['sourceChart'],
  params: {
    x: number;
    y: number;
    measureWidth: number;
    lineHeight: number;
    options: PDFExportOptions;
  }
): void {
  const { x, y, measureWidth, lineHeight, options } = params;
  const { chordsFontSize, lyricsFontSize } = options;

  measures.forEach((measure, i) => {
    const measureX = x + (i * measureWidth);

    // Draw measure box
    pdf.setDrawColor(100, 100, 100);
    pdf.setLineWidth(0.2);
    pdf.rect(measureX, y, measureWidth, lineHeight);

    // Annotations above measure (prevent overlap)
    if (options.includeAnnotations && measure.annotations) {
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(chordsFontSize - 2);
      
      if (measure.annotations.section) {
        pdf.text(measure.annotations.section, measureX + 2, y - 2);
      }

      if (measure.annotations.startRepeat) {
        pdf.setFontSize(chordsFontSize + 4);
        pdf.text('|:', measureX + 1, y + lineHeight / 2);
        pdf.setFontSize(chordsFontSize - 2);
      }
      if (measure.annotations.endRepeat) {
        pdf.setFontSize(chordsFontSize + 4);
        pdf.text(':|', measureX + measureWidth - 4, y + lineHeight / 2);
        pdf.setFontSize(chordsFontSize - 2);
      }
    }

    // Chords — X/diagonal pattern mirroring the app's gig-mode layout
    if (options.includeChords && measure.chords) {
      const validChords = measure.chords.filter(c => c && c.trim() !== '');
      const count = Math.min(validChords.length, 4);
      if (count > 0) {
        // Draw divider lines (diagonal for 2, X for 3-4)
        if (count >= 2) {
          drawMeasureDividers(pdf, measureX, y, measureWidth, lineHeight, count);
        }

        // Chord zone: top 90% (no lyrics) or top 62% (with lyrics) of the measure box
        const hasLyrics = !!(options.includeLyrics && measure.lyrics);
        const zoneH = lineHeight * (hasLyrics ? 0.62 : 0.90);
        const zoneY = y + lineHeight * 0.05;

        const fontScale = CHORD_FONT_SCALE[count];
        validChords.slice(0, count).forEach((chord, idx) => {
          if (chord && chord.trim()) {
            const [topPct, leftPct] = CHORD_POSITIONS[count][idx];
            const chordX = measureX + (leftPct / 100) * measureWidth;
            const chordY = zoneY + (topPct / 100) * zoneH;
            renderStyledChord(pdf, chord, chordX, chordY, chordsFontSize * fontScale);
          }
        });
      }
    }

    // Lyrics (bottom of measure, well separated from chords)
    if (options.includeLyrics && measure.lyrics) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(lyricsFontSize);
      const lyricsY = y + (lineHeight * 0.83);
      
      // Truncate lyrics to fit measure width
      const maxChars = Math.floor(measureWidth / (lyricsFontSize * 0.4));
      const displayLyrics = measure.lyrics.length > maxChars 
        ? measure.lyrics.substring(0, maxChars - 2) + '..' 
        : measure.lyrics;
      
      pdf.text(displayLyrics, measureX + measureWidth / 2, lyricsY, { 
        align: 'center',
        maxWidth: measureWidth * 0.9 
      });
    }
  });
}

/**
 * Legacy export using html2canvas (IMAGE-based, larger files)
 * Kept for backwards compatibility
 * @deprecated Use exportChartToPDFVector instead
 */
export async function exportChartToPDF(chart: SavedChartState): Promise<void> {
  try {
    console.log('📄 Starting PDF export...');
    
    // Get the printable chart container
    const container = document.getElementById('printable-chart');
    console.log('🔍 Looking for printable-chart element:', container);
    
    if (!container) {
      console.error('❌ Printable chart element not found in DOM');
      throw new Error('Printable chart element not found');
    }

    // Get all page elements (direct children of printable-chart)
    const pages = container.children;
    console.log('📑 Found pages:', pages.length);

    if (pages.length === 0) {
      throw new Error('No pages found in chart');
    }

    // A4 width in mm (height is auto-calculated based on content)
    const pdfWidth = 210;

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Capture and add each page
    for (let i = 0; i < pages.length; i++) {
      const pageElement = pages[i] as HTMLElement;
      
      console.log(`📸 Capturing page ${i + 1}...`);

      // Capture this page
      const canvas = await html2canvas(pageElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      console.log(`✅ Page ${i + 1} captured:`, {
        width: canvas.width,
        height: canvas.height
      });

      // Convert canvas to image
      const imgData = canvas.toDataURL('image/png');

      // Calculate image dimensions to fit A4
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Add new page if not first page
      if (i > 0) {
        pdf.addPage();
      }

      // Add image to PDF page
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    }

    // Generate filename from song title
    const filename = sanitizeFilename(chart.songTitle || 'chart') + '.pdf';
    console.log('💾 Saving PDF as:', filename);

    // Save the PDF
    pdf.save(filename);

    console.log('✅ PDF exported successfully!');
  } catch (error) {
    console.error('❌ Error exporting PDF:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    throw new Error('Failed to export PDF. Please try again.');
  }
}

/**
 * Sanitize filename by removing invalid characters
 */
function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-z0-9_\-\s]/gi, '') // Remove special chars
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .toLowerCase()
    .slice(0, 50); // Limit length
}

/**
 * Check if browser supports PDF export
 */
export function isPDFExportSupported(): boolean {
  return typeof window !== 'undefined' && 
         typeof document !== 'undefined' &&
         !!document.createElement('canvas').getContext;
}
